import React, { useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton } from '@mui/material';

interface ImageCarouselProps {
  images: Array<{ id: string; imagePath: string; imageName?: string }>;
  onImageClick: (imageIndex: number) => void;
  maxThumbnails?: number;
}

export default function ImageCarousel({ images, onImageClick, maxThumbnails = 5 }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80 }}>No images</Box>;
  }

  const visibleImages = images.slice(currentIndex, currentIndex + maxThumbnails);
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex + maxThumbnails < images.length;

  const handlePrevious = () => {
    if (canGoPrevious) {
      setCurrentIndex(Math.max(0, currentIndex - 1));
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      setCurrentIndex(Math.min(images.length - maxThumbnails, currentIndex + 1));
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton
        size="small"
        onClick={handlePrevious}
        disabled={!canGoPrevious}
        sx={{ flexShrink: 0 }}
        aria-label="previous images"
      >
        <ChevronLeftIcon />
      </IconButton>

      <Box sx={{ display: 'flex', gap: 1, overflow: 'hidden', flex: 1 }}>
        {visibleImages.map((image, index) => {
          const actualIndex = currentIndex + index;
          return (
            <Box
              key={image.id}
              onClick={() => onImageClick(actualIndex)}
              sx={{
                cursor: 'pointer',
                flexShrink: 0,
                width: 80,
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Box
                component="img"
                src={image.imagePath}
                alt={image.imageName || `Image ${actualIndex + 1}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          );
        })}
      </Box>

      <IconButton
        size="small"
        onClick={handleNext}
        disabled={!canGoNext}
        sx={{ flexShrink: 0 }}
        aria-label="next images"
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
}
